import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, Business as BusinessIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCreateEtablissement } from "../../../api/etablissement";
import { CustomLoader } from "../../../components/CustomLoader/CustomLoader";


interface ModalAddEtablissementProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ModalAddEtablissement: React.FC<ModalAddEtablissementProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");

  const { mutate: createEtablissement, isPending } = useCreateEtablissement(
    () => {
      handleClose();
      onSuccess?.();
    },
    onSuccess
  );

  const handleClose = () => {
    setName("");
    setNameError("");
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!name.trim()) {
      setNameError(t("etablissement.nameRequired", "Name is required"));
      return;
    }
    
    if (name.trim().length < 2) {
      setNameError(t("etablissement.nameMinLength", "Name must be at least 2 characters"));
      return;
    }

    setNameError("");
    createEtablissement({ name: name.trim() });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (nameError) {
      setNameError("");
    }
  };

  return (
    <>
      {isPending && <CustomLoader isActive={isPending} />}
      
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "primary.main",
            color: "white",
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon />
            <Typography variant="h6" component="span">
              {t("etablissement.addNew", "Add New Etablissement")}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "white" }}
            disabled={isPending}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("etablissement.addDescription", "Enter the name for the new etablissement")}
              </Typography>
              
              <TextField
                label={t("etablissement.name", "Name")}
                value={name}
                onChange={handleNameChange}
                error={!!nameError}
                helperText={nameError}
                fullWidth
                required
                variant="outlined"
                placeholder={t("etablissement.namePlaceholder", "Enter etablissement name")}
                disabled={isPending}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isPending}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                minWidth: "80px",
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending || !name.trim()}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                minWidth: "80px",
              }}
            >
              {t("common.add", "Add")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};